import { useState, useEffect, useRef } from 'react'
import {
  BarChart3, Users, ZoomIn, ZoomOut, RefreshCw,
  AlertTriangle, Grid3X3, TrendingDown, DollarSign,
  Filter as Funnel, UserCheck, LineChart,
} from 'lucide-react'
import api from '../../../core/services/api'
import GanttCommesse from '../../../components/strategia/GanttCommesse'
import GanttPersonale from '../../../components/strategia/GanttPersonale'
import SemaforoCommesse from '../../../components/strategia/SemaforoCommesse'
import HeatmapCarico from '../../../components/strategia/HeatmapCarico'
import BurndownCommesse from '../../../components/strategia/BurndownCommesse'
import MarginalitaCommesse from '../../../components/strategia/MarginalitaCommesse'
import PipelineOfferte from '../../../components/strategia/PipelineOfferte'
import EfficienzaPersonale from '../../../components/strategia/EfficienzaPersonale'
import RevenueForecast from '../../../components/strategia/RevenueForecast'
import type { CommessaStrategia } from '../../../components/strategia/GanttCommesse'

type Tab =
  | 'semaforo'
  | 'gantt_commesse'
  | 'gantt_personale'
  | 'heatmap'
  | 'burndown'
  | 'marginalita'
  | 'pipeline'
  | 'efficienza'
  | 'forecast'

type Zoom = 'mesi' | 'settimane'

interface GanttData {
  commesse: CommessaStrategia[]
  personale: Record<string, string>
}

interface MargItem {
  commessa_id: string
  valore_offerta: number
  margine_percentuale: number
}

const TABS: { key: Tab; label: string; icon: React.ElementType; group: string }[] = [
  { key: 'semaforo',       label: 'Semaforo',         icon: AlertTriangle,  group: 'Operativo' },
  { key: 'gantt_commesse', label: 'Gantt Commesse',   icon: BarChart3,      group: 'Operativo' },
  { key: 'gantt_personale',label: 'Gantt Personale',  icon: Users,          group: 'Operativo' },
  { key: 'heatmap',        label: 'Carico Risorse',   icon: Grid3X3,        group: 'Operativo' },
  { key: 'burndown',       label: 'Burn-down',        icon: TrendingDown,   group: 'Operativo' },
  { key: 'marginalita',    label: 'Marginalità',      icon: DollarSign,     group: 'Finanziario' },
  { key: 'pipeline',       label: 'Pipeline',         icon: Funnel,         group: 'Finanziario' },
  { key: 'forecast',       label: 'Forecast',         icon: LineChart,      group: 'Finanziario' },
  { key: 'efficienza',     label: 'Efficienza',       icon: UserCheck,      group: 'Storico' },
]

const GANTT_TABS: Tab[] = ['gantt_commesse', 'gantt_personale']

export default function StrategiaPage() {
  const [tab, setTab] = useState<Tab>('semaforo')
  const [zoom, setZoom] = useState<Zoom>('mesi')
  const [ganttData, setGanttData] = useState<GanttData | null>(null)
  const [margData, setMargData] = useState<MargItem[]>([])
  const [loading, setLoading] = useState(true)
  const visited = useRef<Set<Tab>>(new Set(['semaforo']))

  const loadGantt = () => {
    setLoading(true)
    api.get('/v1/commesse/gantt-strategia')
      .then((r: { data: GanttData }) => setGanttData(r.data))
      .catch(() => setGanttData({ commesse: [], personale: {} }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadGantt() }, [])

  // Precarica marginalità (serve per forecast)
  useEffect(() => {
    api.get('/v1/strategia/marginalita')
      .then((r: { data: MargItem[] }) => setMargData(r.data))
      .catch(() => {})
  }, [])

  const handleTabChange = (t: Tab) => {
    visited.current.add(t)
    setTab(t)
  }

  const showZoom = GANTT_TABS.includes(tab)

  const groups = [...new Set(TABS.map(t => t.group))]

  return (
    <div className="flex flex-col h-full min-h-0 p-6 gap-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Strategia</h1>
          <p className="text-sm text-gray-500 mt-0.5">Controllo operativo, finanziario e storico</p>
        </div>
        <div className="flex items-center gap-3">
          {showZoom && (
            <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
              <button onClick={() => setZoom('mesi')} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${zoom === 'mesi' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
                <ZoomOut className="w-3.5 h-3.5" />Mesi
              </button>
              <button onClick={() => setZoom('settimane')} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${zoom === 'settimane' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
                <ZoomIn className="w-3.5 h-3.5" />Settimane
              </button>
            </div>
          )}
          <button onClick={() => { loadGantt() }} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <RefreshCw className="w-4 h-4" />Aggiorna
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-gray-200 mb-4">
        <div className="flex gap-0 overflow-x-auto">
          {groups.map(group => (
            <div key={group} className="flex items-center">
              <span className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide border-r border-gray-200 h-10 flex items-center">
                {group}
              </span>
              {TABS.filter(t => t.group === group).map(t => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`flex items-center gap-2 px-4 h-10 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    tab === t.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
              <span className="text-sm">Caricamento dati...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Gantt tabs need h-full for scroll sync */}
            <div className={tab === 'gantt_commesse' ? 'block h-full' : 'hidden'}>
              {visited.current.has('gantt_commesse') && (
                <GanttCommesse commesse={ganttData?.commesse ?? []} zoom={zoom} />
              )}
            </div>
            <div className={tab === 'gantt_personale' ? 'block h-full' : 'hidden'}>
              {visited.current.has('gantt_personale') && (
                <GanttPersonale commesse={ganttData?.commesse ?? []} personale={ganttData?.personale ?? {}} zoom={zoom} />
              )}
            </div>

            {/* Regular tabs */}
            {tab === 'semaforo' && (
              <SemaforoCommesse commesse={ganttData?.commesse ?? []} />
            )}
            {tab === 'heatmap' && visited.current.has('heatmap') && (
              <HeatmapCarico commesse={ganttData?.commesse ?? []} personale={ganttData?.personale ?? {}} />
            )}
            {tab === 'burndown' && visited.current.has('burndown') && <BurndownCommesse />}
            {tab === 'marginalita' && visited.current.has('marginalita') && <MarginalitaCommesse />}
            {tab === 'pipeline' && visited.current.has('pipeline') && <PipelineOfferte />}
            {tab === 'efficienza' && visited.current.has('efficienza') && <EfficienzaPersonale />}
            {tab === 'forecast' && visited.current.has('forecast') && (
              <RevenueForecast commesse={ganttData?.commesse ?? []} marginalita={margData} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
